// src/Components/Descargas/DescargasComponent.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { openExternal } from "./openExternal";
import { downloadNativePdf } from "./downloadNativePdf";
import { GetUserData, GetPDFs, DeletePDF } from "../../Backend/ApiCalls";
import { getActiveProfileToken } from "../../utils/profileStorage";
import appleIcon from "../../Assets/Svg/apple-app-store.svg";
import googleIcon from "../../Assets/Svg/google-play-store.svg";
import "./DescargasComponent.css";
import { BottomNav } from "../BottomNav";

const APK_URL = "https://fallaplazarodrigo.es/downloads/app-release.apk";
const IOS_URL = "https://apps.apple.com/us/app/falla-plaza-rodrigo/id6737492055";
const API_BASE = process.env.REACT_APP_API_BASE || "https://api.fallaplazarodrigo.es:999";

// --------- helpers ----------
const fmtSize = (bytes) => {
    if (!Number.isFinite(bytes)) return "—";
    const kb = 1024, mb = kb * 1024, gb = mb * 1024;
    if (bytes >= gb) return (bytes / gb).toFixed(2) + " GB";
    if (bytes >= mb) return (bytes / mb).toFixed(1) + " MB";
    if (bytes >= kb) return Math.round(bytes / kb) + " KB";
    return bytes + " B";
};

// parsea 'YYYY-MM-DD' -> Date a mediodía (evita desfases de timezone)
const coerceDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === "string") {
        // si viene solo YYYY-MM-DD, añadimos hora para evitar TZ raras
        const s = v.length > 10 ? v : `${v}T12:00:00`;
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
};

// fecha “oficial” del acta para ordenar/mostrar
const getActaDate = (p) =>
    coerceDate(p.acta_date) || (p.createdAt ? new Date(p.createdAt) : null);

// año “oficial” del acta para agrupar
const getActaYear = (p) => {
    const d = coerceDate(p.acta_date);
    if (d) return d.getFullYear();
    if (p.acta_year) return Number(p.acta_year);
    if (p.createdAt) return new Date(p.createdAt).getFullYear();
    return new Date().getFullYear();
};
// ----------------------------

const DescargasComponent = (props) => {
    const [pdfs, setPdfs] = useState([]);
    const [user, setUser] = useState(props.user || null);
    const [isAdmin, setIsAdmin] = useState(props.user?.userRank === 3);
    const [selectedYear, setSelectedYear] = useState(null);
    const [busyUpload, setBusyUpload] = useState(false);

    const navigate = useNavigate();
    const isCapacitor = typeof window !== "undefined" && !!window.Capacitor;

    const token = getActiveProfileToken();
    const withToken = (url) =>
        !token ? url : `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;

    useEffect(() => {
        if (props.user) {
            setUser(props.user);
            setIsAdmin(props.user?.userRank === 3);
            return;
        }

        (async () => {
            const { user, isAdmin } = await GetUserData();
            setUser(user);
            setIsAdmin(isAdmin);
        })();
    }, [props.user]);

    const fetchPDFs = async () => {
        const { data } = await GetPDFs();
        const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setPdfs(arr || []);
        // justo después de setPdfs(arr || []);
        console.table((arr || []).map(p => ({
            id: p.Id,
            acta_date: String(p.acta_date),
            parsed: getActaDate(p)?.toISOString(),
            createdAt: p.createdAt
        })));

    };

    useEffect(() => {
        if (!user) return;
        fetchPDFs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // === Agrupar por año usando acta_date como preferencia ===
    const byYear = useMemo(() => {
        const map = new Map();
        for (const p of pdfs) {
            const y = getActaYear(p);
            if (!map.has(y)) map.set(y, []);
            map.get(y).push(p);
        }
        // ordenar por fecha del acta DESC (fallback createdAt)
        for (const arr of map.values()) {
            arr.sort((a, b) => {
                const da = getActaDate(a)?.getTime() ?? 0;
                const db = getActaDate(b)?.getTime() ?? 0;
                return db - da;
            });
        }
        return map;
    }, [pdfs]);

    const years = useMemo(
        () => Array.from(byYear.keys()).sort((a, b) => b - a),
        [byYear]
    );

    useEffect(() => {
        if (years.length && (selectedYear === null || !years.includes(selectedYear))) {
            setSelectedYear(years[0]);
        }
    }, [years, selectedYear]);

    const list = useMemo(() => {
        if (selectedYear == null) return [];
        return byYear.get(selectedYear) || [];
    }, [byYear, selectedYear]);

    // === Acciones ===
    // Detector simple de móvil para el navegador web. En Capacitor ya sabemos
    // que es nativo, así que no nos hace falta este check.
    const isMobileBrowser = () => {
        if (typeof navigator === "undefined") return false;
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    };

    const handleView = async (pdf) => {
        const url = withToken(pdf.fileUrl);

        // En la app nativa (iOS / Android), abrimos el PDF con el visor del
        // sistema (SFSafariViewController en iOS, Chrome Custom Tabs en Android)
        // a través de @capacitor/browser. Eso da el comportamiento "tipo
        // WhatsApp": pantalla completa, scroll y pinch zoom nativos, botón de
        // compartir del sistema, sin el footer/BottomNav de la app encima.
        if (isCapacitor) {
            await openExternal(url);
            return;
        }

        // En el navegador móvil (Safari iOS / Chrome Android) el `<embed>` se
        // ve fatal embebido dentro del layout. Abrimos en pestaña nueva para
        // que el sistema use su renderer nativo de PDF a pantalla completa.
        if (isMobileBrowser()) {
            try {
                window.open(url, "_blank", "noopener,noreferrer");
            } catch (_) { /* noop */ }
            return;
        }

        // Solo en escritorio dejamos el visor interno (con `<embed>`/pdf.js).
        navigate(`/actas/${pdf.Id}`, { state: { acta: pdf } });
    };

    const handleDownload = async (pdf) => {
        const url = withToken(pdf.downloadUrl || pdf.fileUrl);
        if (isCapacitor) {
            await downloadNativePdf({
                url,
                fileName: pdf.fileName || pdf.title || "documento.pdf",
            });
            return;
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = pdf.fileName || "documento.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handleDelete = async (pdfId) => {
        if (!window.confirm("¿Seguro que deseas eliminar este PDF?")) return;
        const { data } = await DeletePDF(pdfId);
        if (data?.message || data?.Message) {
            setPdfs((prev) => prev.filter((x) => x.Id !== pdfId));
            alert("PDF eliminado correctamente.");
        } else {
            alert("Error al eliminar el PDF.");
        }
    };

    // === Subida (solo admin) ===
    const onSubmitUpload = async (e) => {
        e.preventDefault();
        if (busyUpload) return;

        const form = e.currentTarget;
        const file = form.pdf.files?.[0];
        if (!file) return alert("Selecciona un PDF.");

        const fd = new FormData();
        fd.append("pdf", file);
        if (form.title.value) fd.append("title", form.title.value);
        if (form.acta_date.value) fd.append("acta_date", form.acta_date.value);

        try {
            setBusyUpload(true);
            const res = await fetch(`${API_BASE}/PDF/upload`, {
                method: "POST",
                headers: token ? { token } : undefined,
                body: fd,
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.error || "Error subiendo PDF");

            await fetchPDFs();
            form.reset();
            alert("Archivo subido correctamente.");
        } catch (err) {
            console.error(err);
            alert(String(err.message || err));
        } finally {
            setBusyUpload(false);
        }
    };

    return (
        <div className="pdf-manager-container">
            <h2 className="titulo-page">Zona de Descargas</h2>
                  <BottomNav I18n={props.I18n} user={user} />

            {/* enlaces a tiendas */}
            <div className="download-links">
                <a href={IOS_URL} target="_blank" rel="noopener noreferrer">
                    <img src={appleIcon} alt="App Store" />
                </a>
                <a href={APK_URL} target="_blank" rel="noopener noreferrer">
                    <img src={googleIcon} alt="Google Play" />
                </a>
            </div>

            {/* ===== Panel de años ===== */}
            {years.length > 0 && (
                <div className="paginado">
                    {years.map((y) => (
                        <button
                            key={y}
                            className={selectedYear === y ? "selected" : ""}
                            onClick={() => setSelectedYear(y)}
                        >
                            {y}
                        </button>
                    ))}
                </div>
            )}

            {/* ===== Formulario admin ===== */}
            {isAdmin && (
                <form className="upload-card" onSubmit={onSubmitUpload}>
                    <h3>Subir nueva acta (PDF)</h3>
                    <div className="grid">
                        <label>
                            Título
                            <input name="title" type="text" placeholder="Ej. ACTA AC FALLA..." />
                        </label>
                        <label>
                            Fecha del acta
                            <input name="acta_date" type="date" />
                        </label>
                        <label className="file-label">
                            Archivo PDF
                            <input name="pdf" type="file" accept="application/pdf" required />
                        </label>
                    </div>
                    <button type="submit" className="view-btn" disabled={busyUpload}>
                        {busyUpload ? "Subiendo..." : "Subir PDF"}
                    </button>
                </form>
            )}

            {/* ===== Lista por año (ordenada por acta_date DESC) ===== */}
            <h3 className="titulo-page">Actas {selectedYear ? `(${selectedYear})` : ""}</h3>

            {list.length > 0 ? (
                <ul className="pdf-list">
                    {list.map((pdf) => {
                        // fecha preferente: acta_date
                        const d = getActaDate(pdf);
                        return (
                            <li key={pdf.Id} className="pdf-item">
                                <div className="pdf-meta">
                                    <div className="pdf-title">{pdf.title}</div>
                                    <div className="pdf-sub">
                                        <span>{d ? d.toLocaleDateString() : "Sin fecha"}</span>
                                        <span>•</span>
                                        <span>{fmtSize(pdf.sizeBytes)}</span>
                                        <span>•</span>
                                        <span className="filename">{pdf.fileName}</span>
                                    </div>
                                </div>
                                <div className="pdf-buttons">
                                    <button className="view-btn" onClick={() => handleView(pdf)}>Ver</button>
                                    <button className="download-btn" onClick={() => handleDownload(pdf)}>Descargar</button>
                                </div>
                                {isAdmin && (
                                    <div className="pdf-actions">
                                        <button className="danger" onClick={() => handleDelete(pdf.Id)}>Eliminar</button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="no-pdfs">No hay actas para este año.</p>
            )}
        </div>
    );
};

export default DescargasComponent;
